/* eslint-disable prettier/prettier */
import JwtService from "@/core/services/jwt.service";
import { http } from "@/configurations/axios";
import { priceOptions, quantityOptions } from '@/modules/Application'
import { adminPermissionStore } from "@/adminPermissionStore";
import { store as StoreVenda } from '@/modules/Vendas/storeVenda.js'
import { store as storeNfe } from '@/modules/NFe/storeNfe.js'
import { store as storePedidos } from '@/modules/Pedidos/storePedidos.js'
import { store as StoreOrcamento } from '@/modules/Orcamento/storeOrcamento.js'
import { store as StoreOS } from '@/modules/OS/storeOS.js'
import { store as StoreServicos } from '@/modules/Servicos/storeServicos.js'
import { clienteInadimplenciaStore } from "@/modules/Application/stores/ClienteInadimplenciaStore";
import { setUserAccess, userAccessStore } from "@/userAccessStore";

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const toBoolean = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const backendBoolean = (...values) => {
  const value = firstDefined(...values);
  return value === undefined ? undefined : toBoolean(value);
};

const shouldRequestAdminPermission = (user = {}) => {
  if (backendBoolean(user?.admin) === true) return false;

  return backendBoolean(user?.configuracoes?.solicita_senha_adm) ?? false;
};

const normalizeRevendaId = (value) => {
  if (value === undefined || value === null || value === "") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

const buildAccountFlags = (user = {}, fallback = {}) => {
  const empresa = user?.empresa || {};
  const usuarioEmpresa = user?.usuario_empresa?.empresa || {};
  const dadosRevenda = empresa?.dados_revenda || {};
  const idRevenda = normalizeRevendaId(firstDefined(
    dadosRevenda?.id_revenda,
    empresa?.id_revenda,
    user?.id_revenda,
    fallback.idRevenda,
    null
  ));
  const isNuts =
    backendBoolean(
      empresa?.isNuts,
      empresa?.is_nuts,
      user?.isNuts,
      user?.is_nuts
    ) ?? false;
  const legacyDadosRevendaObject =
    empresa?.dados_revenda && Object.keys(empresa.dados_revenda).length > 0;
  const isRevenda =
    backendBoolean(
      // Prefer backend explicit flags.
      user?.isRevenda,
      user?.is_revenda,
      user?.revenda,
      empresa?.isRevenda,
      empresa?.is_revenda,
      empresa?.revenda,
      usuarioEmpresa?.isRevenda,
      usuarioEmpresa?.is_revenda,
      usuarioEmpresa?.revenda,
      empresa?.dados_revenda?.isRevenda,
      empresa?.dados_revenda?.is_revenda,
      empresa?.dados_revenda?.revenda,
      fallback.isRevenda
    ) ?? legacyDadosRevendaObject ?? false;
  const isClienteRevenda =
    backendBoolean(
      empresa?.isClienteRevenda,
      empresa?.is_cliente_revenda,
      user?.isClienteRevenda,
      user?.is_cliente_revenda
    ) ?? (!isNuts && !isRevenda && !!idRevenda && idRevenda !== 1);
  const isClienteNutsDireto =
    backendBoolean(
      empresa?.isClienteNutsDireto,
      empresa?.is_cliente_nuts_direto,
      user?.isClienteNutsDireto,
      user?.is_cliente_nuts_direto
    ) ?? (!isNuts && !isRevenda && idRevenda === 1);

  return {
    idRevenda,
    isNuts,
    isRevenda,
    isClienteRevenda,
    isClienteNutsDireto,
  };
};

// action types
export const VERIFY_AUTH = "verifyAuth";
export const LOGIN = "login";
export const LOGOUT = "logout";
export const REGISTER = "register";
export const VERIFY_ASSINATURA = "verifyAssinatura";
export const LOAD_EMPRESAS = "loadEmpresas";
export const SELECT_EMPRESA = "selectEmpresa";
export const UPDATE_EMPRESA_PADRAO = "updateEmpresaPadrao";

// mutation types
export const PURGE_AUTH = "logOut";
export const SET_AUTH = "setUser";
export const SET_ERROR = "setError";
export const SET_ASSINATURA = "setAssinatura";
export const SET_MOSTRAR_ATALHOS_PDV = "setMostrarAtalhosPdv";
export const SET_EMPRESAS = "setEmpresas";
export const SET_EMPRESA_CONTEXT = "setEmpresaContext";
export const SET_EMPRESA_PADRAO = "setEmpresaPadrao";

const state = {
  errors: null,
  user: {},
  isAuthenticated: !!JwtService.getToken(),
  itsblocked: false,
  isNuts: false,
  isRevenda: false,
  isClienteRevenda: false,
  isClienteNutsDireto: false,
  idRevenda: null,
  dadosRevenda: {},
  nutsDigital: false,
  licenca: null,
  vendedor: {},
  empresa: {
    features: [],
  },
  empresas: [],
  empresaContexto: null,
  empresaPadraoId: null,
};

const getters = {
  currentUser(state) {
    return state.user;
  },
  isAuthenticated(state) {
    return state.isAuthenticated;
  },
  itsblocked(state) {
    return state.itsblocked;
  },
  isNuts(state) {
    return state.isNuts;
  },
  isRevenda(state) {
    return state.isRevenda;
  },
  isClienteRevenda(state) {
    return state.isClienteRevenda;
  },
  isClienteNutsDireto(state) {
    return state.isClienteNutsDireto;
  },
  idRevenda(state) {
    return state.idRevenda;
  },
  dadosRevenda(state) {
    return state.dadosRevenda;
  },
  nutsDigital(state) {
    return state.nutsDigital;
  },
  licenca(state) {
    return state.licenca;
  },
  vendedor(state) {
    return state.vendedor;
  },
  empresa(state) {
    return state.empresa;
  },
  empresas(state) {
    return state.empresas;
  },
  empresaContexto(state) {
    return state.empresaContexto;
  },
  empresaPadraoId(state) {
    return state.empresaPadraoId;
  },
  features(state) {
    return state.empresa?.features || [];
  },
  hasFeature: (state) => (code) => {
    return (state.empresa?.features || []).includes(code);
  }
};

const actions = {
  [LOGIN](context, credentials) {
    return new Promise((resolve, reject) => {
      http
        .post("/autenticacao", credentials)
        .then(async ({ data }) => {
          context.commit(SET_AUTH, data);
          await context.dispatch(VERIFY_AUTH);
          resolve(data);
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },
  [LOAD_EMPRESAS](context) {
    return http
      .get("/autenticacao/empresas")
      .then(({ data }) => {
        context.commit(SET_EMPRESAS, data);
        return data;
      });
  },
  [SELECT_EMPRESA](context, payload) {
    const options = typeof payload === "object"
      ? payload
      : { id_empresa: payload };
    const idEmpresa = Number(options?.id_empresa);

    if (!Number.isInteger(idEmpresa) || idEmpresa <= 0) {
      return Promise.reject(new Error("Empresa inválida."));
    }

    return http
      .post("/autenticacao/selecionar-empresa", {
        id_empresa: idEmpresa,
        definir_como_padrao: options?.definir_como_padrao === true,
      })
      .then(async ({ data }) => {
        JwtService.saveEmpresaId(idEmpresa);
        context.commit(SET_EMPRESA_CONTEXT, data?.data || {
          id_empresa: idEmpresa,
        });
        await context.dispatch(VERIFY_AUTH);
        return data;
      });
  },
  [UPDATE_EMPRESA_PADRAO](context, payload) {
    const idEmpresa = Number(payload?.id_empresa ?? payload);

    if (!Number.isInteger(idEmpresa) || idEmpresa <= 0) {
      return Promise.reject(new Error("Empresa inválida."));
    }

    return http
      .put("/autenticacao/empresa-padrao", {
        id_empresa: idEmpresa,
      })
      .then(({ data }) => {
        context.commit(
          SET_EMPRESA_PADRAO,
          data?.data?.id_empresa_padrao ?? idEmpresa
        );
        return data;
      });
  },
  [LOGOUT](context) {
    return new Promise((resolve, reject) => {
      http
        .post("/autenticacao/logout")
        .then(async ({ data }) => {
          context.commit(PURGE_AUTH);
          resolve(data);
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },
  [REGISTER](context, credentials) {
    return new Promise((resolve, reject) => {
      http
        .post("/empresa/create", credentials)
        .then(async ({ data }) => {
          await context.dispatch(LOGIN, {
            email: credentials.email,
            password: credentials.password
          });
          resolve(data);
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },
  [VERIFY_AUTH](context) {
    if (JwtService.getToken()) {
      return http
        .get("/autenticacao/dados")
        .then(({ data }) => {
          quantityOptions.precision = data?.configuracoes?.casas_decimal_quantidade;
          priceOptions.precision = data?.configuracoes?.casas_decimal_preco;
          StoreVenda.aberturaCaixa = data?.configuracoes?.abertura_caixa ?? false;
          StoreVenda.fechamentoAsCegas = data?.configuracoes?.fechamento_as_cegas ?? false;
          StoreVenda.verificaEstoque = data?.configuracoes?.verifica_estoque ?? false;
          storeNfe.verificaEstoque = data?.configuracoes?.verifica_estoque ?? false;
          storePedidos.verificaEstoque = data?.configuracoes?.verifica_estoque ?? false;
          StoreVenda.naoGeraImpressao = data?.configuracoes?.nao_gerar_impressao ?? false;
          StoreVenda.vendedorObrigatorio = data?.configuracoes?.vendedor_obrigatorio ?? false;
          StoreVenda.desconsiderarEstoqueCondicional = data?.configuracoes?.desconsiderar_estoque_condicional ?? false;
          storePedidos.pafNFCe = data?.paf_nfce ?? false;
          StoreVenda.pafNFCe = data?.paf_nfce ?? false;
          StoreOrcamento.pafNFCe = data?.paf_nfce ?? false;
          StoreOS.pafNFCe = data?.paf_nfce ?? false;
          StoreServicos.informacoesComplementares = data?.configuracoes.informacao_nfse ?? null;
          StoreOS.trabalharHorasOs = data?.configuracoes?.trabalhar_horas_os ?? false;
          StoreOS.trabalharNumeroInternoOs = data?.configuracoes?.trabalhar_numero_interno_os ?? false;
          storeNfe.importarOBSProduto = data?.configuracoes?.importar_obs_produto ?? false;
          clienteInadimplenciaStore.bloquearVendaClienteInadimplente = data?.configuracoes?.bloquear_venda_cliente_inadimplente ?? false;
          clienteInadimplenciaStore.limiteInadimplenciaPermitida = data?.configuracoes?.limite_inadimplencia_permitida ?? 0;
          context.commit(SET_AUTH, data);
        })
        .catch(({ response }) => {
          context.commit(SET_ERROR, response?.data?.errors || response?.data);
        });
    } else {
      context.commit(PURGE_AUTH);
      return Promise.resolve(); // <- importantíssimo para poder dar await
    }
  },
  [VERIFY_ASSINATURA](context) {
    if (JwtService.getToken()) {
      return http
        .post("/empresa/assinatura")
        .then(({ data }) => {
          if (data) {
            quantityOptions.precision = data?.configuracoes?.casas_decimal_quantidade;
            priceOptions.precision = data?.configuracoes?.casas_decimal_preco;
            StoreVenda.aberturaCaixa = data?.configuracoes?.abertura_caixa ?? false;
            StoreVenda.fechamentoAsCegas = data?.configuracoes?.fechamento_as_cegas ?? false;
            StoreVenda.verificaEstoque = data?.configuracoes?.verifica_estoque ?? false;
            storeNfe.verificaEstoque = data?.configuracoes?.verifica_estoque ?? false;
            storePedidos.verificaEstoque = data?.configuracoes?.verifica_estoque ?? false;
            StoreVenda.naoGeraImpressao = data?.configuracoes?.nao_gerar_impressao ?? false;
            StoreVenda.vendedorObrigatorio = data?.configuracoes?.vendedor_obrigatorio ?? false;
            StoreVenda.desconsiderarEstoqueCondicional = data?.configuracoes?.desconsiderar_estoque_condicional ?? false;
            storePedidos.pafNFCe = data?.paf_nfce ?? false;
            StoreVenda.pafNFCe = data?.paf_nfce ?? false;
            StoreOrcamento.pafNFCe = data?.paf_nfce ?? false;
            StoreOS.pafNFCe = data?.paf_nfce ?? false;
            StoreServicos.informacoesComplementares = data?.configuracoes.informacao_nfse ?? null;
            StoreOS.trabalharHorasOs = data?.configuracoes?.trabalhar_horas_os ?? false;
            StoreOS.trabalharNumeroInternoOs = data?.configuracoes?.trabalhar_numero_interno_os ?? false;
            storeNfe.importarOBSProduto = data?.configuracoes?.importar_obs_produto ?? false;
            clienteInadimplenciaStore.bloquearVendaClienteInadimplente = data?.configuracoes?.bloquear_venda_cliente_inadimplente ?? false;
            clienteInadimplenciaStore.limiteInadimplenciaPermitida = data?.configuracoes?.limite_inadimplencia_permitida ?? 0;


            context.commit(SET_ASSINATURA, data);
          }
        })
        .catch(({ response }) => {
          context.commit(SET_ERROR, response?.data?.errors || response?.data);
        });
    } else {
      context.commit(PURGE_AUTH);
      return Promise.resolve();
    }
  }
};

const mutations = {
  [SET_ERROR](state, error) {
    state.errors = error;
  },
  [SET_AUTH](state, user) {
    user = user || {};
    const empresa = user?.empresa || {};
    const assinatura = empresa?.assinatura || {};
    const dadosRevenda = empresa?.dados_revenda || {};
    const accountFlags = buildAccountFlags(user, state);

    state.isAuthenticated = true;
    state.errors = {};
    state.user = user;
    const empresaContexto = user?.empresa_contexto || null;

    if (empresaContexto?.id_empresa) {
      JwtService.saveEmpresaId(empresaContexto.id_empresa);
    }

    state.empresaContexto = empresaContexto;
    state.empresaPadraoId = user?.id_empresa_padrao
      ? Number(user.id_empresa_padrao)
      : null;
    if (Array.isArray(user?.empresas)) {
      state.empresas = user.empresas;
    }
    adminPermissionStore.requestPermission = shouldRequestAdminPermission(user);
    state.empresa = {
      ...empresa,
      features: Array.isArray(empresa?.features) ? empresa.features : [],
    };
    setUserAccess(user?.recursos || []);
    state.itsblocked = assinatura?.bloqueado ?? false;
    if (user.token) {
      JwtService.saveToken(state.user.token);
    }
    state.isNuts = accountFlags.isNuts;
    state.isRevenda = accountFlags.isRevenda;
    state.isClienteRevenda = accountFlags.isClienteRevenda;
    state.isClienteNutsDireto = accountFlags.isClienteNutsDireto;
    if (state.isRevenda) {
      JwtService.saveParceiro(state.isRevenda);
    }
    state.idRevenda = accountFlags.idRevenda;
    state.dadosRevenda = dadosRevenda;
    state.nutsDigital = user?.nuts_digital ?? false;
    state.licenca = user;
    if (user.vendedor !== undefined) {
      state.vendedor = user.vendedor || {};
    }
  },
  [PURGE_AUTH](state) {
    state.isAuthenticated = false;
    state.user = {};
    state.errors = {};
    JwtService.destroyToken();
    JwtService.destroyTokenCliente();
    JwtService.destroyParceiro();;
    JwtService.destroyEmpresaId();
    state.itsblocked = false;
    state.isNuts = false;
    state.isRevenda = false;
    state.isClienteRevenda = false;
    state.isClienteNutsDireto = false;
    state.idRevenda = null;
    state.dadosRevenda = {};
    state.nutsDigital = null;
    state.licenca = null;
    state.vendedor = {};
    state.empresa = { features: [] };
    state.empresas = [];
    state.empresaContexto = null;
    state.empresaPadraoId = null;
    adminPermissionStore.requestPermission = false;
    userAccessStore.access = {};
    userAccessStore.disabled = {};
    userAccessStore.tooltips = {};
  },
  [SET_MOSTRAR_ATALHOS_PDV](state, valor) {
    const mostrar = !!valor;
    if (state.user && typeof state.user === "object") {
      state.user = { ...state.user, mostrar_atalhos_pdv: mostrar };
    }
  },
  [SET_EMPRESAS](state, payload) {
    state.empresas = Array.isArray(payload?.data) ? payload.data : [];
    state.empresaPadraoId = payload?.id_empresa_padrao
      ? Number(payload.id_empresa_padrao)
      : null;

    if (payload?.id_empresa_atual) {
      JwtService.saveEmpresaId(payload.id_empresa_atual);
      const contextoAtual = state.empresas.find(
        empresa => Number(empresa.id_empresa) === Number(payload.id_empresa_atual)
      );

      state.empresaContexto = contextoAtual || {
        id_empresa: Number(payload.id_empresa_atual),
      };
    }
  },
  [SET_EMPRESA_CONTEXT](state, contexto) {
    state.empresaContexto = contexto || null;
    state.user = {
      ...state.user,
      empresa_contexto: contexto || null,
    };
  },
  [SET_EMPRESA_PADRAO](state, idEmpresa) {
    state.empresaPadraoId = idEmpresa ? Number(idEmpresa) : null;
    state.user = {
      ...state.user,
      id_empresa_padrao: state.empresaPadraoId,
    };
  },
  [SET_ASSINATURA](state, data) {
    state.itsblocked = data.bloqueado;
    state.isNuts = backendBoolean(data.nuts, data.isNuts, data.is_nuts) ?? state.isNuts;
    state.isRevenda =
      backendBoolean(data.revenda, data.isRevenda, data.is_revenda) ??
      state.isRevenda;
    state.idRevenda = normalizeRevendaId(
      firstDefined(data.id_revenda, data.idRevenda, state.idRevenda)
    );
    state.isClienteRevenda =
      backendBoolean(
        data.isClienteRevenda,
        data.is_cliente_revenda,
        data.cliente_revenda
      ) ??
      (!state.isNuts &&
        !state.isRevenda &&
        !!state.idRevenda &&
        state.idRevenda !== 1);
    state.isClienteNutsDireto =
      backendBoolean(
        data.isClienteNutsDireto,
        data.is_cliente_nuts_direto,
        data.cliente_nuts_direto
      ) ??
      (!state.isNuts && !state.isRevenda && state.idRevenda === 1);
    state.dadosRevenda.id_revenda = state.idRevenda;
    state.dadosRevenda.nome_revenda =
      data.nome_revenda ?? state.dadosRevenda.nome_revenda;
    state.dadosRevenda.fone_revenda =
      data.fone_revenda ?? state.dadosRevenda.fone_revenda;
    state.nutsDigital = data.nuts_digital ?? state.nutsDigital;
    state.licenca = data;
  }
};

export default {
  state,
  actions,
  mutations,
  getters,
};
